import {
  deriveAppointmentDisplayStatus,
  deriveCurrentStepId,
  deriveQuestionnaireState,
  type QuestionnaireStateInput,
} from '@/lib/courseStepStatus';
import type { BookedStepStatus } from '@/types/treatmentCourses';

export const COURSE_ACTION_KINDS = [
  'missing_journal',
  'unanswered_questionnaire',
  'unbooked',
  'outside_window',
] as const;

export type CourseActionKind = (typeof COURSE_ACTION_KINDS)[number];

/**
 * Temporary: sent and failed (unanswered) questionnaires do not count as
 * «Kræver handling» until a real response deadline exists. The course
 * overview uses the same switch for the full amber questionnaire chip.
 * While it is off, «Ikke besvaret» only colors the chip icon amber; the
 * label and background stay neutral. Missing journal, an unbooked current
 * step, and a time outside the window are unchanged — including their
 * highlighting.
 *
 * Flip back to true when that deadline lands. That restores both the action
 * and the amber chip together.
 */
export const UNANSWERED_QUESTIONNAIRES_REQUIRE_ACTION = false;

export type CourseActionStepInput = {
  id: string;
  sort_order: number;
  status: BookedStepStatus;
  appointment_id?: string | null;
  appointment_status?: string | null;
  has_journal_note?: boolean;
  questionnaires?: Array<
    QuestionnaireStateInput & {
      forms_template_id: string;
      template_name?: string | null;
      assignment_id?: string | null;
    }
  >;
};

export type CourseActionItem = {
  id: string;
  kind: CourseActionKind;
  stepId: string;
  sortOrder: number;
  questionnaireId?: string;
  questionnaireName?: string;
};

export function unansweredQuestionnaireState(
  input: QuestionnaireStateInput,
): boolean {
  const state = deriveQuestionnaireState(input).state;
  switch (state) {
    case 'sent':
    case 'failed':
      return true;
    case 'planned':
    case 'completed':
    case 'skipped':
      return false;
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export function stepMissingJournal(step: CourseActionStepInput): boolean {
  if (!step.appointment_id) return false;
  if (step.has_journal_note) return false;
  return step.status === 'completed';
}

export function stepNeedsBooking(
  steps: readonly CourseActionStepInput[],
  step: CourseActionStepInput,
): boolean {
  const appointmentStatus = deriveAppointmentDisplayStatus({
    stepStatus: step.status,
    appointmentId: step.appointment_id,
    appointmentStatus: step.appointment_status,
  });
  if (appointmentStatus !== 'not_booked') return false;
  if (step.status === 'cancelled') return false;
  return deriveCurrentStepId(steps) === step.id;
}

export function deriveCourseActionItems(
  steps: readonly CourseActionStepInput[],
  brokenStepIds: ReadonlySet<string> = new Set(),
  options?: { includeUnansweredQuestionnaires?: boolean },
): CourseActionItem[] {
  const includeUnanswered =
    options?.includeUnansweredQuestionnaires ?? UNANSWERED_QUESTIONNAIRES_REQUIRE_ACTION;
  const items: CourseActionItem[] = [];
  for (const step of steps) {
    if (step.status === 'cancelled') continue;
    if (stepMissingJournal(step)) {
      items.push({
        id: `${step.id}:missing_journal`,
        kind: 'missing_journal',
        stepId: step.id,
        sortOrder: step.sort_order,
      });
    }
    if (includeUnanswered) {
      for (const row of step.questionnaires ?? []) {
        const raw = row as QuestionnaireStateInput & {
          job_status?: string | null;
          scheduled_for?: string | null;
          attempted_at?: string | null;
          assignment_status?: string | null;
          sent_at?: string | null;
          completed_at?: string | null;
        };
        const input: QuestionnaireStateInput = {
          jobStatus: raw.jobStatus ?? raw.job_status,
          scheduledFor: raw.scheduledFor ?? raw.scheduled_for,
          attemptedAt: raw.attemptedAt ?? raw.attempted_at,
          assignmentStatus: raw.assignmentStatus ?? raw.assignment_status,
          sentAt: raw.sentAt ?? raw.sent_at,
          completedAt: raw.completedAt ?? raw.completed_at,
        };
        if (!unansweredQuestionnaireState(input)) continue;
        items.push({
          id: `${step.id}:q:${row.forms_template_id}`,
          kind: 'unanswered_questionnaire',
          stepId: step.id,
          sortOrder: step.sort_order,
          questionnaireId: row.forms_template_id,
          questionnaireName: row.template_name ?? undefined,
        });
      }
    }
    if (stepNeedsBooking(steps, step)) {
      items.push({
        id: `${step.id}:unbooked`,
        kind: 'unbooked',
        stepId: step.id,
        sortOrder: step.sort_order,
      });
    }
    if (brokenStepIds.has(step.id)) {
      items.push({
        id: `${step.id}:outside_window`,
        kind: 'outside_window',
        stepId: step.id,
        sortOrder: step.sort_order,
      });
    }
  }
  return items;
}

export function stepHasAction(
  items: readonly CourseActionItem[],
  stepId: string,
): boolean {
  return items.some((item) => item.stepId === stepId);
}
